import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'

import { AddButton } from 'components/AddButton'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { ProtocolsTable } from './ProtocolsTable'
import { Pagination } from 'components/Pagination'

import { all } from 'store/Protocols/Protocols.selector'
import { protocolsActions } from 'store/Protocols'

import { submitOnEnter } from 'utils/submitOnEnter'
import { useTranslation } from 'react-i18next'

import * as S from './styled'

const FILTERS_INITIAL_STATE = {
  name: '',
  code: '',
  limit: 10,
  page: 0
}

const ERRORS_INITIAL_STATE = {
  name: '',
  code: ''
}

export function ProtocolsList() {
  const { t } = useTranslation()
  const [filter, setFilter] = useState({ ...FILTERS_INITIAL_STATE })
  const [errors, setErrors] = useState({ ...ERRORS_INITIAL_STATE })

  const { protocols } = useSelector(all)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(protocolsActions.cleanProtocols())
    dispatch(protocolsActions.fetchAll())
  }, [dispatch])

  function handleInputData(value: string, name: string) {
    setErrors({ ...errors, [name]: '' })
    setFilter({ ...filter, [name]: value })
  }

  async function onFilter() {
    try {
      const filterPayload = await Yup.object()
        .shape({
          code: Yup.string(),
          name: Yup.string()
        })
        .validate(filter, {
          abortEarly: false,
          stripUnknown: true
        })

      dispatch(protocolsActions.fetchAll(filterPayload))
    } catch (ex) {
      const pairs = ex.inner.map(({ path, message }) => [
        path,
        message.replace(`${path} `, '')
      ])
      setErrors(Object.fromEntries(pairs) as typeof FILTERS_INITIAL_STATE)
    }
  }

  function onFilterPaginate(params = {}) {
    dispatch(protocolsActions.fetchAll(params))
  }

  function clearFilter() {
    const params = {
      ...FILTERS_INITIAL_STATE,
      limit: protocols.pagination?.limit ?? FILTERS_INITIAL_STATE.limit,
      page: protocols.pagination?.page ?? FILTERS_INITIAL_STATE.page
    }
    setFilter(params)
    setErrors({ ...ERRORS_INITIAL_STATE })
    dispatch(protocolsActions.fetchAll(params))
  }

  return (
    <S.Wrapper container>
      <S.ButtonAdd>
        <Link to="/protocolos/novo">
          <AddButton />
        </Link>
      </S.ButtonAdd>
      <S.FullGrid item xs={12}>
        <S.GridHeader>
          <S.GridTitle item container xs={12}>
            <S.BoxTitle>{t('common:filterProtocols')}</S.BoxTitle>
            <S.BoxLine />
          </S.GridTitle>
        </S.GridHeader>
        <S.GridFilter container>
          <S.GridInput item sm={12} md={6} lg={4}>
            <Input
              error={Boolean(errors.name)}
              fullWidth
              helperText={errors.name}
              label={t('protocols:name')}
              onInput={value => handleInputData(value, 'name')}
              onKeyPress={submitOnEnter(onFilter)}
              value={filter.name}
            />
          </S.GridInput>
          <S.GridInput item sm={12} md={6} lg={4}>
            <Input
              error={Boolean(errors.code)}
              fullWidth
              helperText={errors.code}
              label={t('protocols:code')}
              onKeyPress={submitOnEnter(onFilter)}
              onInput={value => handleInputData(value, 'code')}
              value={filter.code}
            />
          </S.GridInput>
          <S.GridButtons item sm={12} md={12} lg={4}>
            <Button variant="default" onClick={clearFilter} size="medium">
              <p>{t('common:clean')}</p>
            </Button>
            <Button variant="primary" onClick={onFilter} size="medium">
              <p>{t('common:search')}</p>
            </Button>
          </S.GridButtons>
        </S.GridFilter>
        <S.GridTable>
          <ProtocolsTable rows={protocols.items} />
          {protocols.pagination?.totalItems > 0 && (
            <Pagination
              filters={{
                ...filter,
                limit: protocols.pagination.limit,
                page: protocols.pagination.page
              }}
              limit={protocols.pagination.limit}
              page={protocols.pagination.page}
              totalItems={protocols.pagination.totalItems}
              setFilter={onFilterPaginate}
            />
          )}
        </S.GridTable>
      </S.FullGrid>
    </S.Wrapper>
  )
}
